import {
  useEffect, useMemo, useState, 
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import useFocusedModule from 'hooks/useFocusedModule';
import useLanguage from 'hooks/useLanguage';
import ProductConverterResult from 'components/ProductConverterResult';
import ProductConverterInitial from 'components/ProductConverterInitial';
import useFile from 'api/hooks/useFile';
import { unMaskInput } from 'utils/helpers';
import { AlkaliProps } from 'utils/types';

export interface IData {
  alkali: string;
  concentration: string;
  consumption: string;
  price: string;
  annualExpenditure: string | number;
  omya: string;
  concentrationOmya: string;
  costDelivered: string;
}

const ProductConverter = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { data } = useFile({ type: 'converter' });
  const [comparisonData, setComparisonData] = useState<IData | null>(null);
  const [result, setResult] = useState<boolean>(false);
  const { blueLang } = useLanguage();

  useFocusedModule('Product Converter Module Duration');

  useEffect(() => {
    if (blueLang.length > 0) {
      i18n.changeLanguage(blueLang);
    }
  }, [blueLang]);

  const filteredData = useMemo(() => {
    const omyaData: AlkaliProps[] = [];
    const productData: AlkaliProps[] = [];
    data?.map((products) => {
      if (products?.['Material A']?.includes('Omya')) {
        omyaData.push(products);
      } else {
        productData.push(products);
      }
    });

    return { omyaData,
      productData };
  }, [data]);

  const expectedProductConsumption = useMemo(() => {
    const currentAlkali = filteredData?.productData?.find(
      (product) => product['Material A'] === comparisonData?.alkali &&
        product?.Concentration === Number(comparisonData?.concentration),
    );
    const currentOmya = filteredData?.omyaData?.find(
      (product) => product['Material A'] === comparisonData?.omya &&
        product?.Concentration === Number(comparisonData?.concentrationOmya),
    );

    if (
      !currentAlkali ||
      !currentOmya ||
      !comparisonData?.consumption ||
      !comparisonData?.concentration ||
      !comparisonData?.concentrationOmya
    ) {
      return 0;
    }

    const alkaliConsumption = Number(unMaskInput(comparisonData?.consumption));
    const alkaliEq =
      currentAlkali?.Stoichiometry / currentAlkali?.['Mol. Weight'];

    const alkaliConcentration = Number(comparisonData?.concentration);
    const alkaliValue = alkaliConsumption * alkaliEq * alkaliConcentration;

    const omyaEq = currentOmya?.Stoichiometry / currentOmya?.['Mol. Weight'];

    const omyaConcentration = Number(comparisonData?.concentrationOmya);

    const omyaValue = omyaEq * omyaConcentration;

    return alkaliValue / omyaValue;
  }, [comparisonData, filteredData]);

  return (
    <div className="px-[100px] py-[32px] md:px-[16px] md:pb-4">
      <div className="mb-8 md:mb-4">
        <span
          onClick={() => navigate('/')}
          className="text-secondaryText cursor-pointer"
        >
          {t('home')} /
        </span>{' '}
        <span className="font-medium">{t('converter')}</span>
      </div>
      <div className={`${data && !result ? '' : 'hidden'}`}>
        <ProductConverterInitial
          data={filteredData?.productData}
          omyaData={filteredData?.omyaData}
          setComparisonData={setComparisonData}
          setResult={setResult}
        />
      </div>
      <div className={`${comparisonData !== null && result ? '' : 'hidden'}`}>
        {comparisonData !== null && result && (
          <ProductConverterResult
            {...comparisonData}
            setResult={setResult}
            expectedConsumption={Math.round(expectedProductConsumption)}
          />
        )}
      </div>
    </div>
  );
};

export default ProductConverter;
